/** Small numeric helpers used across the evaluator. */

/**
 * A number the calibration / authoring math needs finite was not (NaN, ±Infinity). Thrown by those validators instead of a
 * bare `Error` so a boundary can refuse this one fault and let every other exception — a programming error — propagate.
 */
export class NonFiniteNumericError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NonFiniteNumericError';
  }
}

export function clamp(x: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, x));
}

/** Time discount D(t) = e^{-λt}. */
export function discount(t: number, lambda: number): number {
  return Math.exp(-lambda * t);
}

/** Whole tick count for a dot's `tickS`/`durationS` shape — same `Math.floor(durationS/tickS + 1e-9)` `eval/effects.ts`'s dot pricing case uses; the single source of truth reused by dot magnitude authoring (`generation/numeric/dot-shape.ts`) so the two never drift. */
export function dotTickCount(tickS: number, durationS: number): number {
  return Math.floor(durationS / tickS + 1e-9);
}

/** Poisson PMF P(X = k) for mean λ. */
function poissonPmf(k: number, lambda: number): number {
  if (k < 0) return 0;
  // exp(k·ln λ − λ − ln k!)
  let logFact = 0;
  for (let i = 2; i <= k; i++) logFact += Math.log(i);
  return Math.exp(k * Math.log(lambda) - lambda - logFact);
}

/** P(X ≥ j) for Poisson(λ). */
function poissonSurvival(j: number, lambda: number): number {
  if (j <= 0) return 1;
  let cdf = 0;
  for (let k = 0; k < j; k++) cdf += poissonPmf(k, lambda);
  return clamp(1 - cdf, 0, 1);
}

/**
 * E[min(cap, X)] for X ~ Poisson(λ) using E[min(c,X)] = Σ_{j=1}^{c} P(X ≥ j).
 */
export function poissonExpectedMin(cap: number, lambda: number): number {
  if (!(lambda > 0)) return 0;
  let sum = 0;
  for (let j = 1; j <= cap; j++) sum += poissonSurvival(j, lambda);
  return sum;
}

export function isFiniteNumber(x: unknown): x is number {
  return typeof x === 'number' && Number.isFinite(x);
}

/**
 * Expected geometric-weighted hit count for an unlimited-pierce, falloff
 * projectile (2026-09-10 encounter-density-policy decision 10, Phase 4b).
 * The projectile's corridor encounters beyond the guaranteed primary are
 * modeled the same way as everywhere else in this module — a Poisson
 * arrival process with mean `lambda` — and each hit ordinal k (0-indexed)
 * contributes `rate^k` of a full target instead of a full target, so the
 * weighted total is:
 *
 *   guaranteedPrimary: E[Σ_{k=0}^{X} rate^k]     (primary always lands at k=0)
 *   otherwise:         E[Σ_{k=0}^{X-1} rate^k]   (X hits total, no separate primary)
 *
 * with X ~ Poisson(lambda). Both reduce to closed forms via the Poisson PGF
 * `E[rate^X] = exp(-lambda·(1-rate))`:
 *
 *   guaranteedPrimary: (1 - rate·E[rate^X]) / (1-rate) = 1 + rate·(non-guaranteed form)
 *   otherwise:         (1 - E[rate^X]) / (1-rate)
 *
 * Limits (verified in `math.test.ts` / `eval-targeting-projectile-corridor.test.ts`):
 *  - rate→0: guaranteedPrimary → exactly 1 (today's non-piercing collapse);
 *    non-guaranteed → exactly `1 - e^-lambda` (today's `maxPierce ?? 1`,
 *    non-guaranteed `poissonExpectedMin(1, lambda)` value).
 *  - rate→1: guaranteedPrimary → `1 + lambda`; non-guaranteed → `lambda`
 *    (the falloff-free uncapped-corridor limit — no falloff means every
 *    corridor encounter counts as a full target).
 */
export function poissonPierceFalloffExpected(lambda: number, rate: number, guaranteedPrimary: boolean): number {
  const f = clamp(rate, 0, 1);
  if (lambda <= 0) return guaranteedPrimary ? 1 : 0;
  if (Math.abs(1 - f) < 1e-9) return guaranteedPrimary ? 1 + lambda : lambda;
  const expectedFPowX = Math.exp(-lambda * (1 - f));
  return guaranteedPrimary ? (1 - f * expectedFPowX) / (1 - f) : (1 - expectedFPowX) / (1 - f);
}
